import type { HRLeaveBalance, HRLeaveType } from '@/api/client'
import { cn } from '@/lib/cn'
import { useTranslation } from 'react-i18next'

interface Props {
  balance: HRLeaveBalance
  leaveType?: HRLeaveType | null
  className?: string
}

// LeaveBalanceBar visualises taken (solid zinc) + pending (muted zinc) days
// against the yearly accrual. The numeric summary always shows one decimal
// so 12 → 12.0 stays aligned across rows.
export function LeaveBalanceBar({ balance, leaveType, className }: Props) {
  const { t } = useTranslation('hr')
  const accrued = balance.accrued_days ?? 0
  const taken = balance.taken_days ?? 0
  const pending = balance.pending_days ?? 0
  const available = balance.available_days ?? accrued - taken - pending

  const takenPct = accrued > 0 ? Math.min(100, (taken / accrued) * 100) : 0
  const pendingPct = accrued > 0 ? Math.min(100 - takenPct, (pending / accrued) * 100) : 0

  const name = leaveType?.name ?? balance.leave_type_name ?? balance.leave_type_code ?? ''

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">{name}</span>
        <span className="tabular-nums text-foreground">
          <span className="font-bold">{available.toFixed(1)}</span>
          <span className="ml-1 text-xs text-muted-foreground">
            / {accrued.toFixed(1)} j
          </span>
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        aria-hidden="true"
      >
        <div className="flex h-full">
          <div className="h-full bg-primary" style={{ width: `${takenPct}%` }} />
          <div
            className="h-full bg-muted-foreground"
            style={{ width: `${pendingPct}%` }}
          />
        </div>
      </div>
      {pending > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('leaves.balance_pending', { days: pending.toFixed(1) })}
        </p>
      ) : null}
    </div>
  )
}
